import { useEffect, useState } from "react";
import { Button, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  GoMakeAutoComplate,
  GoMakeModal,
  GomakePrimaryButton,
} from "@/components";
import { FONT_FAMILY } from "@/utils/font-family";
import { useGomakeAxios, useSnackBar } from "@/hooks";
import { returnResult } from "@/utils/helpers";
import { useStyle } from "../style";

interface SelectExistingParameterModalProps {
  openModal: boolean;
  onClose: () => void;
  templateId?: string;
  sectionId?: string;
  subSectionId?: string;
  getProductById: () => void;
  onAddExisting?: (sectionId: string, subSectionId: string, parameter: any) => boolean;
}

const SelectExistingParameterModal = ({
  openModal,
  onClose,
  templateId,
  sectionId,
  subSectionId,
  getProductById,
  onAddExisting,
}: SelectExistingParameterModalProps) => {
  const { t } = useTranslation();
  const { classes } = useStyle();
  const { callApi } = useGomakeAxios();
  const { alertFaultAdded, alertSuccessAdded, alertFault } = useSnackBar();
  const [parameters, setParameters] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);

  useEffect(() => {
    if (!openModal) {
      return;
    }
    const load = async () => {
      const result = await callApi(
        "GET",
        "/v1/printhouse-config/parameters/get-all-gomake-parameters",
      );
      const data = returnResult(result, undefined);
      setParameters(Array.isArray(data) ? data : []);
    };
    load();
  }, [callApi, openModal]);

  const onAdd = async () => {
    if (!selected?.id || !templateId || !sectionId || !subSectionId) {
      return;
    }
    if (onAddExisting) {
      const queued = onAddExisting(sectionId, subSectionId, selected);
      if (!queued) {
        alertFault(t("products.parameters.parameterAlreadyExists", { defaultValue: "This parameter already exists" }));
        return;
      }
      setSelected(null);
      onClose();
      return;
    }
    const result: any = await callApi(
      "POST",
      "/v1/printhouse-config/templates/add-existing-parameter",
      {
        templateId,
        sectionId,
        subSectionId,
        parameterId: selected.id,
      },
    );
    if (result?.success) {
      alertSuccessAdded();
      getProductById();
      setSelected(null);
      onClose();
      return;
    }
    if (result?.errors?.ErrorMessage) {
      alertFault(result.errors.ErrorMessage);
      return;
    }
    alertFaultAdded();
  };

  return (
    <GoMakeModal
      openModal={openModal}
      modalTitle={t("products.parameters.parameterName")}
      onClose={onClose}
      modalTitleStyle={{
        ...FONT_FAMILY.Inter(600, 18),
        paddingBottom: "20px",
      }}
      modalTitleColor={"#000000"}
      insideStyle={classes.insideStyle}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <span>{t("products.parameters.parameterName")}</span>
        <GoMakeAutoComplate
          options={parameters}
          placeholder={t("products.parameters.parameterName")}
          getOptionLabel={(option: any) =>
            option?.name || option?.code || ""
          }
          value={selected}
          onChange={(_event: any, value: any) => setSelected(value)}
          style={{ border: "1px solid #E0E0E0" }}
        />
        <Stack direction={"row"} gap={"10px"}>
          <Button style={classes.cancelButton} onClick={onClose}>
            {t("modal.cancel")}
          </Button>
          <GomakePrimaryButton
            style={{ width: "50%", borderRadius: "8px", height: "44px" }}
            onClick={onAdd}
          >
            {t("products.parameters.addNewParameter")}
          </GomakePrimaryButton>
        </Stack>
      </div>
    </GoMakeModal>
  );
};

export { SelectExistingParameterModal };
